import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Col, Form, Row } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

const defaultImage = `${process.env.PUBLIC_URL}/event-default.svg`;

function authConfig(token, json = false) {
  const headers = { Authorization: `Bearer ${token}` };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function eventDateLabel(value) {
  if (!value) {
    return "";
  }
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function typeNames(item) {
  return (item?.entry_options || []).map((row) => row.title).join(", ");
}

function clockLabel(value) {
  if (!value) {
    return "";
  }
  const [hourText, minute = "00"] = String(value).split(":");
  let hour = Number(hourText);
  if (!Number.isFinite(hour)) {
    return "";
  }
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
}

function inputTime(value) {
  return (value || "").slice(0, 5);
}

function scheduleClock(line) {
  const start = clockLabel(line.starts);
  const end = clockLabel(line.ends);
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start;
}

function EventSection({ title, children, open: openProp, onToggle }) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp === undefined ? ownOpen : openProp;
  const toggle = () => {
    if (onToggle) {
      onToggle();
      return;
    }
    setOwnOpen((value) => !value);
  };
  return (
    <section className={open ? "home-admin-card is-open" : "home-admin-card"}>
      <h3>
        <button type="button" aria-expanded={open} onClick={toggle}>
          {title}
          <span className="home-admin-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="accordion-fold">
        <div className="accordion-panel" inert={!open}>
          <div className="home-admin-panel">{children}</div>
        </div>
      </div>
    </section>
  );
}

function EventSchedule({ eventId, token }) {
  const [lines, setLines] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [label, setLabel] = useState("");
  const [ask, confirmDialog] = useConfirm();
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`/api/admin/events/${eventId}/schedule/`, authConfig(token));
    setLines(data);
    setError("");
  };

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
    // The loader only needs to run when this event changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  const openNew = () => {
    setEditing("new");
    setStarts("");
    setEnds("");
    setLabel("");
    setError("");
  };

  const openEdit = (line) => {
    setEditing(line);
    setStarts(inputTime(line.starts));
    setEnds(inputTime(line.ends));
    setLabel(line.label);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    const body = { starts, ends, label };
    try {
      if (editing === "new") {
        await axios.post(`/api/admin/events/${eventId}/schedule/`, body, authConfig(token, true));
      } else {
        await axios.put(`/api/admin/event-schedule/${editing.id}/`, body, authConfig(token, true));
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const remove = async (line) => {
    if (!(await ask(removePrompt(line.label)))) {
      return;
    }
    setError("");
    try {
      await axios.delete(`/api/admin/event-schedule/${line.id}/`, authConfig(token));
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const applyOrder = async (next) => {
    const previous = lines;
    setLines(next);
    setError("");
    try {
      const { data } = await axios.put(
        `/api/admin/events/${eventId}/schedule/order/`,
        { ids: next.map((line) => line.id) },
        authConfig(token, true)
      );
      setLines(data);
    } catch (err) {
      setLines(previous);
      setError(errorText(err));
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= lines.length || from === to) {
      return;
    }
    const next = lines.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    applyOrder(next);
  };

  const dropOn = (targetId, sourceId) => {
    const from = lines.findIndex((line) => line.id === sourceId);
    const to = lines.findIndex((line) => line.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from < 0 || to < 0) {
      return;
    }
    move(from, to);
  };

  return (
    <EventSection title="Schedule">
      {confirmDialog}
      <p>Times for this day only. Drag a row to change the order.</p>
      {error ? <Message variant="danger">{error}</Message> : null}
      <div className="admin-sheet-wrap">
        <table className="admin-sheet schedule-sheet">
          <thead>
            <tr>
              <th className="schedule-grip-col">
                <span className="visually-hidden">Order</span>
              </th>
              <th>Time</th>
              <th colSpan={2}>
                <div className="admin-sheet-head">
                  <span>What happens</span>
                  <AdminAddButton onClick={openNew}>
                    <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                    </svg>
                    Add a schedule entry
                  </AdminAddButton>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={4}>No schedule lines yet.</td>
              </tr>
            ) : (
              lines.map((line, index) => (
                <tr
                  key={line.id}
                  className={overId === line.id ? "is-over" : ""}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setOverId(line.id);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    dropOn(line.id, Number(event.dataTransfer.getData("text/plain")));
                  }}
                >
                  <td>
                    <button
                      type="button"
                      className="schedule-grip"
                      draggable
                      aria-label={`Move ${line.label}`}
                      title={`Move ${line.label}`}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(line.id));
                        setDragId(line.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverId(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowUp") {
                          event.preventDefault();
                          move(index, index - 1);
                        }
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          move(index, index + 1);
                        }
                      }}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <circle cx="5" cy="3.5" r="1.2" />
                        <circle cx="11" cy="3.5" r="1.2" />
                        <circle cx="5" cy="8" r="1.2" />
                        <circle cx="11" cy="8" r="1.2" />
                        <circle cx="5" cy="12.5" r="1.2" />
                        <circle cx="11" cy="12.5" r="1.2" />
                      </svg>
                    </button>
                  </td>
                  <td className={dragId === line.id ? "is-dragging" : ""}>{scheduleClock(line)}</td>
                  <td className={dragId === line.id ? "is-dragging schedule-what" : "schedule-what"}>{line.label}</td>
                  <td>
                    <RecordActions name={line.label} onEdit={() => openEdit(line)} onRemove={() => remove(line)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminFormModal
        show={Boolean(editing)}
        title={editing === "new" ? "Add a schedule entry" : "Edit schedule entry"}
        onHide={() => setEditing(null)}
      >
        <Form onSubmit={save}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`schedule-time-${eventId}`}>
                <Form.Label>Starts</Form.Label>
                <Form.Control type="time" value={starts} onChange={(e) => setStarts(e.target.value)} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId={`schedule-end-${eventId}`}>
                <Form.Label>Ends</Form.Label>
                <Form.Control type="time" value={ends} onChange={(e) => setEnds(e.target.value)} />
                <p className="field-note">Optional. Leave this blank for a single time.</p>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3" controlId={`schedule-label-${eventId}`}>
            <Form.Label>What happens</Form.Label>
            <Form.Control value={label} onChange={(e) => setLabel(e.target.value)} required />
          </Form.Group>
          {error ? <Message variant="danger">{error}</Message> : null}
          <AdminFormActions
            saveLabel={editing === "new" ? "Add schedule entry" : "Save schedule entry"}
            onCancel={() => setEditing(null)}
          />
        </Form>
      </AdminFormModal>
    </EventSection>
  );
}

function EventRules({ eventId, token }) {
  const [lines, setLines] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");
  const [ask, confirmDialog] = useConfirm();
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`/api/admin/events/${eventId}/rules/`, authConfig(token));
    setLines(data);
    setError("");
  };

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
    // The loader only needs to run when this event changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  const openNew = () => {
    setEditing("new");
    setText("");
    setError("");
  };

  const openEdit = (line) => {
    setEditing(line);
    setText(line.text);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    try {
      if (editing === "new") {
        await axios.post(`/api/admin/events/${eventId}/rules/`, { text }, authConfig(token, true));
      } else {
        await axios.put(`/api/admin/event-rules/${editing.id}/`, { text }, authConfig(token, true));
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const remove = async (line) => {
    if (!(await ask(removePrompt(line.text)))) {
      return;
    }
    setError("");
    try {
      await axios.delete(`/api/admin/event-rules/${line.id}/`, authConfig(token));
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const applyOrder = async (next) => {
    const previous = lines;
    setLines(next);
    setError("");
    try {
      const { data } = await axios.put(
        `/api/admin/events/${eventId}/rules/order/`,
        { ids: next.map((line) => line.id) },
        authConfig(token, true)
      );
      setLines(data);
    } catch (err) {
      setLines(previous);
      setError(errorText(err));
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= lines.length || from === to) {
      return;
    }
    const next = lines.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    applyOrder(next);
  };

  const dropOn = (targetId, sourceId) => {
    const from = lines.findIndex((line) => line.id === sourceId);
    const to = lines.findIndex((line) => line.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from < 0 || to < 0) {
      return;
    }
    move(from, to);
  };

  return (
    <EventSection title="Rules">
      {confirmDialog}
      <p>Rules for entering this show. Drag a row to change the order.</p>
      {error ? <Message variant="danger">{error}</Message> : null}
      <div className="admin-sheet-wrap">
        <table className="admin-sheet schedule-sheet rule-sheet">
          <thead>
            <tr>
              <th className="schedule-grip-col">
                <span className="visually-hidden">Order</span>
              </th>
              <th colSpan={2}>
                <div className="admin-sheet-head">
                  <span>Rule</span>
                  <AdminAddButton onClick={openNew}>
                    <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                    </svg>
                    Add a rule
                  </AdminAddButton>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={3}>No rules yet.</td>
              </tr>
            ) : (
              lines.map((line, index) => (
                <tr
                  key={line.id}
                  className={overId === line.id ? "is-over" : ""}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setOverId(line.id);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    dropOn(line.id, Number(event.dataTransfer.getData("text/plain")));
                  }}
                >
                  <td>
                    <button
                      type="button"
                      className="schedule-grip"
                      draggable
                      aria-label={`Move ${line.text}`}
                      title={`Move ${line.text}`}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(line.id));
                        setDragId(line.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverId(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowUp") {
                          event.preventDefault();
                          move(index, index - 1);
                        }
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          move(index, index + 1);
                        }
                      }}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <circle cx="5" cy="3.5" r="1.2" />
                        <circle cx="11" cy="3.5" r="1.2" />
                        <circle cx="5" cy="8" r="1.2" />
                        <circle cx="11" cy="8" r="1.2" />
                        <circle cx="5" cy="12.5" r="1.2" />
                        <circle cx="11" cy="12.5" r="1.2" />
                      </svg>
                    </button>
                  </td>
                  <td className={dragId === line.id ? "is-dragging schedule-what" : "schedule-what"}>{line.text}</td>
                  <td>
                    <RecordActions name={line.text} onEdit={() => openEdit(line)} onRemove={() => remove(line)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminFormModal
        show={Boolean(editing)}
        title={editing === "new" ? "Add a rule" : "Edit rule"}
        onHide={() => setEditing(null)}
      >
        <Form onSubmit={save}>
          <Form.Group className="mb-3" controlId={`rule-text-${eventId}`}>
            <Form.Label>Rule</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
            />
          </Form.Group>
          {error ? <Message variant="danger">{error}</Message> : null}
          <AdminFormActions
            saveLabel={editing === "new" ? "Add rule" : "Save rule"}
            onCancel={() => setEditing(null)}
          />
        </Form>
      </AdminFormModal>
    </EventSection>
  );
}

function EventClubs({ eventId, token }) {
  const [lines, setLines] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [clubId, setClubId] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const { ask, confirmDialog } = useConfirm();

  const load = async () => {
    const config = authConfig(token);
    const [assigned, clubs] = await Promise.all([
      axios.get(`/api/admin/events/${eventId}/clubs/`, config),
      axios.get("/api/admin/clubs/", config),
    ]);
    setLines(assigned.data);
    setCatalog(clubs.data);
  };

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
  }, [eventId, token]);

  const assignedIds = new Set(lines.map((line) => String(line.club)));
  const available = catalog.filter((club) => !assignedIds.has(String(club.id)));

  const openAdd = () => {
    setError("");
    setClubId(available[0] ? String(available[0].id) : "");
    setOpen(true);
  };

  const addClub = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await axios.post(
        `/api/admin/events/${eventId}/clubs/`,
        { club: clubId },
        authConfig(token, true)
      );
      setOpen(false);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeClub = async (line) => {
    if (!(await ask(removePrompt(line.title)))) {
      return;
    }
    setError("");
    try {
      await axios.delete(`/api/admin/event-clubs/${line.id}/`, authConfig(token));
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <EventSection title="Clubs">
      {confirmDialog}
      <p>Clubs coming to this show. They appear on the event page.</p>
      {error && !open ? <Message variant="danger">{error}</Message> : null}
      <div className="admin-sheet-wrap">
        <table className="admin-sheet">
          <thead>
            <tr>
              <th>Logo</th>
              <th colSpan={2}>
                <div className="admin-sheet-head">
                  <span>Club</span>
                  <AdminAddButton onClick={openAdd}>
                    <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                    </svg>
                    Add a club
                  </AdminAddButton>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={3}>No clubs on this show yet.</td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.id}>
                  <td>
                    {line.logo_thumb_url ? (
                      <img className="admin-sheet-thumb" src={line.logo_thumb_url} alt="" />
                    ) : null}
                  </td>
                  <td className="admin-sheet-name">{line.title}</td>
                  <td>
                    <RecordActions name={line.title} onRemove={() => removeClub(line)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminFormModal show={open} title="Add a club" onHide={() => setOpen(false)}>
        {available.length === 0 ? (
          <>
            <p>{catalog.length === 0 ? "Add a club on the Clubs page first." : "Every club is already on this show."}</p>
            <div className="admin-form-actions">
              <button type="button" className="btn btn-site-quiet" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </>
        ) : (
          <Form onSubmit={addClub}>
            <Form.Group className="mb-3" controlId={`event-club-${eventId}`}>
              <Form.Label>Club</Form.Label>
              <Form.Select value={clubId} onChange={(e) => setClubId(e.target.value)} required>
                {available.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {error ? <Message variant="danger">{error}</Message> : null}
            <AdminFormActions saveLabel="Add club" onCancel={() => setOpen(false)} />
          </Form>
        )}
      </AdminFormModal>
    </EventSection>
  );
}

function cardImage(item) {
  return item.image_secondary_url || item.image_main_url || defaultImage;
}

function EventHeading({ view, onList }) {
  return (
    <div className="event-heading">
      <h2>Events</h2>
      {view !== "list" ? (
        <button type="button" className="event-heading-link" onClick={onList}>
          View Events
        </button>
      ) : null}
    </div>
  );
}

function EventFields({ form, setField, fileKey, photos, entryTypes, toggleType }) {
  return (
    <>
      <Form.Group className="mb-3" controlId="eventTitle">
        <Form.Label>Title</Form.Label>
        <Form.Control value={form.title} onChange={(e) => setField("title", e.target.value)} required />
      </Form.Group>
      <fieldset className="mb-3 type-checks">
        <legend>Type</legend>
        <p className="field-note">
          Checked types show on the registration form. Add a new name under Types in Admin, then check it here.
        </p>
        {entryTypes.length === 0 ? (
          <p>No types yet. Add one under Types.</p>
        ) : (
          entryTypes.map((item) => {
            const checked = form.entry_types.includes(String(item.id));
            return (
              <label key={item.id} className={checked ? "type-check is-on" : "type-check"}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleType(item.id)}
                />
                <span>{item.title}</span>
              </label>
            );
          })
        )}
      </fieldset>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="eventDate">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={form.date}
              onChange={(e) => setField("date", e.target.value)}
              required
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="entryFee">
            <Form.Label>Car entry fee</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={form.entry_fee}
              onChange={(e) => setField("entry_fee", e.target.value)}
              placeholder="0.00"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="spectatorFee">
            <Form.Label>Spectator ticket price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={form.spectator_fee}
              onChange={(e) => setField("spectator_fee", e.target.value)}
              placeholder="0.00"
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3" controlId="eventDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
        />
      </Form.Group>
      <Row>
        {[
          ["image_main", "Main image", photos?.image_main_url],
          ["image_secondary", "Secondary image", photos?.image_secondary_url],
          ["image_mobile", "Mobile image", photos?.image_mobile_url],
        ].map(([name, label, current]) => (
          <Col md={4} key={name}>
            <Form.Group className="mb-3" controlId={name}>
              <Form.Label>{label}</Form.Label>
              {current && <img className="event-photo d-block mb-2" src={current} alt="" />}
              <Form.Control
                key={`${name}-${fileKey}`}
                type="file"
                accept="image/*"
                onChange={(e) => setField(name, e.target.files?.[0] || null)}
              />
              {photos && (
                <Form.Text className="text-muted">
                  If you upload a new image, it replaces the current one.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        ))}
      </Row>
    </>
  );
}


function EventGallery({ eventId, token }) {
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [photographer, setPhotographer] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [credit, setCredit] = useState(null);
  const [creditName, setCreditName] = useState("");
  const [ask, confirmDialog] = useConfirm();

  const load = async () => {
    const { data } = await axios.get(`/api/admin/events/${eventId}/photos/`, authConfig(token));
    setPhotos(data);
    setError("");
  };

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
    // load is recreated each render and only needs to run when the event changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  const upload = async (event) => {
    event.preventDefault();
    if (files.length === 0) {
      return;
    }
    setBusy(true);
    setError("");
    setStatus("");
    const batchSize = 15;
    const skipped = [];
    try {
      for (let index = 0; index < files.length; index += batchSize) {
        const batch = files.slice(index, index + batchSize);
        setStatus(`Uploading ${Math.min(index + batch.length, files.length)} of ${files.length}`);
        const formData = new FormData();
        formData.append("photographer", photographer.trim());
        batch.forEach((file) => formData.append("photos", file));
        const { data } = await axios.post(
          `/api/admin/events/${eventId}/photos/`,
          formData,
          authConfig(token)
        );
        if (data.detail) {
          skipped.push(data.detail);
        }
      }
      setFiles([]);
      setPhotographer("");
      setFileKey((current) => current + 1);
      setAddOpen(false);
      await load();
      setStatus(
        skipped.length
          ? `Photos saved. ${skipped.join(" ")}`
          : `${files.length} photo${files.length === 1 ? "" : "s"} saved.`
      );
    } catch (err) {
      setError(errorText(err));
      await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const setUse = async (photo, key, checked) => {
    setPhotos((current) =>
      current.map((item) => (item.id === photo.id ? { ...item, [key]: checked } : item))
    );
    try {
      const { data } = await axios.patch(
        `/api/admin/event-photos/${photo.id}/`,
        { [key]: checked },
        authConfig(token, true)
      );
      setPhotos((current) => current.map((item) => (item.id === data.id ? data : item)));
      setError("");
    } catch (err) {
      setPhotos((current) =>
        current.map((item) => (item.id === photo.id ? { ...item, [key]: photo[key] } : item))
      );
      setError(errorText(err));
    }
  };

  const saveCredit = async (photo, next) => {
    try {
      const { data } = await axios.patch(
        `/api/admin/event-photos/${photo.id}/`,
        { photographer: next },
        authConfig(token, true)
      );
      setPhotos((current) => current.map((item) => (item.id === data.id ? data : item)));
      setError("");
    } catch (err) {
      setError(errorText(err));
      throw err;
    }
  };

  const remove = async (photo) => {
    const name = photo.photographer ? `the photo by ${photo.photographer}` : "this event photo";
    if (!(await ask(removePrompt(name)))) {
      return;
    }
    setError("");
    try {
      await axios.delete(`/api/admin/event-photos/${photo.id}/`, authConfig(token));
      setPhotos((current) => current.filter((item) => item.id !== photo.id));
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <EventSection title="Photographer gallery">
      {confirmDialog}
      <p>
        Hired photographer photos for this event. Add them in batches. An event can hold well over 100.
        The photographer name is saved on every photo in that upload. Check any mix. Gallery keeps the
        photo in the gallery. Home card puts it on the home page cards. Hero image puts it at the top of
        the show. Leave Gallery off to take that photo out of the gallery.
      </p>
      {error && <Message variant="danger">{error}</Message>}
      {status && <Message variant="info">{status}</Message>}
      <div className="admin-sheet-wrap">
        <table className="admin-sheet">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Photographer</th>
              <th colSpan={2}>
                <div className="admin-sheet-head">
                  <span>Use on</span>
                  <AdminAddButton onClick={() => setAddOpen(true)}>
                    <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                    </svg>
                    Add photos
                  </AdminAddButton>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {photos.length === 0 ? (
              <tr>
                <td colSpan={4}>No photos yet.</td>
              </tr>
            ) : (
              photos.map((photo) => {
                const name = photo.photographer ? `the photo by ${photo.photographer}` : "this event photo";
                return (
                  <tr key={photo.id}>
                    <td>
                      <img
                        className="admin-sheet-thumb"
                        src={photo.thumb_url}
                        alt={photo.photographer ? `Photo by ${photo.photographer}` : "Event gallery photo"}
                      />
                    </td>
                    <td>{photo.photographer || "Photographer not named"}</td>
                    <td>
                      <div className="photo-uses">
                        {[
                          ["on_gallery", "Gallery"],
                          ["on_home", "Home card"],
                          ["on_hero", "Hero image"],
                        ].map(([key, label]) => {
                          const choice = `${label} for ${name}`;
                          return (
                            <label key={key}>
                              <input
                                type="checkbox"
                                checked={Boolean(photo[key])}
                                aria-label={choice}
                                title={choice}
                                onChange={(event) => setUse(photo, key, event.target.checked)}
                              />
                              {label}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <RecordActions
                        name={name}
                        onEdit={() => {
                          setCredit(photo);
                          setCreditName(photo.photographer || "");
                        }}
                        onRemove={() => remove(photo)}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <AdminFormModal show={addOpen} title="Add photos" onHide={() => setAddOpen(false)}>
      <Form onSubmit={upload} className="mb-3">
        <Form.Group className="mb-3" controlId="eventPhotos">
          <Form.Label>Photos</Form.Label>
          <Form.Control
            key={fileKey}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </Form.Group>
        <Form.Group className="mb-3 photo-credit-all" controlId="eventPhotographer">
          <Form.Label>Photographer</Form.Label>
          <Form.Control
            value={photographer}
            onChange={(e) => setPhotographer(e.target.value)}
            required
          />
        </Form.Group>
          <AdminFormActions
            saveLabel={busy ? "Uploading" : "Add photos"}
            busy={busy}
            disabled={files.length === 0}
            onCancel={() => setAddOpen(false)}
          />
      </Form>
      </AdminFormModal>
      <AdminFormModal
        show={Boolean(credit)}
        title="Edit photographer"
        onHide={() => setCredit(null)}
      >
        <Form
          onSubmit={async (event) => {
            event.preventDefault();
            await saveCredit(credit, creditName.trim());
            setCredit(null);
          }}
        >
          <Form.Group className="mb-3" controlId="photoCredit">
            <Form.Label>Photographer</Form.Label>
            <Form.Control value={creditName} onChange={(e) => setCreditName(e.target.value)} />
          </Form.Group>
          <AdminFormActions saveLabel="Save photographer" onCancel={() => setCredit(null)} />
        </Form>
      </AdminFormModal>
    </EventSection>
  );
}

function blankClassForm() {
  return { car_class: "", section: "", number: "", parking_location: "", trophies: {} };
}

function trophySheet(trophies) {
  const rows = trophies || [];
  const street = rows.filter((trophy) => trophy.division === "street_race");
  const concours = rows.filter((trophy) => trophy.division === "concours");
  if (street.length || concours.length) {
    return {
      heading: "Divisions",
      places: ["First", "Second", "Third"],
      rows: [
        { key: "street", label: "Street/Race", trophies: street },
        { key: "concours", label: "Concours", trophies: concours },
      ],
    };
  }
  return {
    heading: "Trophies",
    places: rows.map((trophy) => trophy.title),
    rows: [{ key: "places", label: "", trophies: rows }],
  };
}

function trophyHolder(trophy, entered) {
  const holder = (entered || []).find((entry) => String(entry.id) === String(trophy.registration));
  return holder ? holder.label : "N/A";
}

function trophyChoices(trophy, entries, selected, current) {
  const taken = new Set(
    Object.entries(selected || {})
      .filter(([id, value]) => value && String(id) !== String(trophy.id))
      .map(([, value]) => String(value))
  );
  return (entries || []).filter((entry) => {
    if (String(entry.id) === String(current)) {
      return true;
    }
    if (taken.has(String(entry.id))) {
      return false;
    }
    if (entry.division === "display") {
      return false;
    }
    if (trophy.division && entry.division !== trophy.division) {
      return false;
    }
    return true;
  });
}

function TrophyPhotoColumn({ title, trophyTitle, kind, photos, busy, onUpload, onRemove }) {
  const rows = photos || [];
  const noun = kind === "trophy" ? "this trophy photo" : "this winner photo";
  const alt = kind === "trophy" ? "Trophy" : "Winner on stage";
  const addName = `Add ${title.toLowerCase()} for ${trophyTitle}`;
  return (
    <div>
      <p className="trophy-column-title">{title}</p>
      {rows.length === 0 ? (
        <p className="trophy-column-empty">No photos yet.</p>
      ) : (
        <ul className="trophy-photo-list">
          {rows.map((photo) => (
            <li key={photo.id}>
              <img src={photo.thumb_url} alt={alt} />
              <RecordActions name={noun} onRemove={() => onRemove(photo)} />
            </li>
          ))}
        </ul>
      )}
      <label className="trophy-photo-add">
        <input
          type="file"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          multiple
          disabled={busy}
          aria-label={addName}
          title={addName}
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = "";
            onUpload(files);
          }}
        />
        {busy ? "Uploading" : "Add photos"}
      </label>
    </div>
  );
}

function TrophySlots({ trophies, entries, selected, onSelect, token, onPhotos }) {
  const [openId, setOpenId] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [ask, confirmDialog] = useConfirm();

  const upload = async (trophy, kind, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) {
      return;
    }
    setBusyKey(`${trophy.id}-${kind}`);
    setError("");
    const formData = new FormData();
    formData.append("kind", kind);
    files.forEach((file) => formData.append("photos", file));
    try {
      const { data } = await axios.post(
        `/api/admin/class-trophies/${trophy.id}/photos/`,
        formData,
        authConfig(token)
      );
      onPhotos(trophy.id, data);
      if (data.detail) {
        setError(data.detail);
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusyKey("");
    }
  };

  const remove = async (trophy, photo, kind) => {
    const name = kind === "trophy" ? "this trophy photo" : "this winner photo";
    if (!(await ask(removePrompt(name)))) {
      return;
    }
    setError("");
    try {
      const { data } = await axios.delete(`/api/admin/class-trophy-photos/${photo.id}/`, authConfig(token));
      onPhotos(trophy.id, data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="trophy-slots">
      {confirmDialog}
      {error ? <Message variant="danger">{error}</Message> : null}
      {(trophies || []).map((trophy) => {
        const open = openId === trophy.id;
        const current = selected[trophy.id] || "";
        const choices = trophyChoices(trophy, entries, selected, current);
        const holder = (entries || []).find((entry) => String(entry.id) === String(current));
        return (
          <div key={trophy.id} className={open ? "trophy-slot is-open" : "trophy-slot"}>
            <h3>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : trophy.id)}
              >
                <span>{trophy.title}</span>
                <span className="trophy-slot-side">
                  <span>{holder ? holder.label : "No one yet"}</span>
                  <span className="trophy-slot-chevron" aria-hidden="true" />
                </span>
              </button>
            </h3>
            <div className="accordion-fold">
              <div className="accordion-panel" inert={!open}>
                <div className="trophy-slot-grid">
                  <Form.Group controlId={`trophy-${trophy.id}`}>
                    <Form.Label>Winner</Form.Label>
                    <Form.Select
                      value={current}
                      onChange={(event) => onSelect(trophy.id, event.target.value)}
                    >
                      <option value="">No one yet</option>
                      {choices.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.division_label ? `${entry.label} · ${entry.division_label}` : entry.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <TrophyPhotoColumn
                    title="Trophy photos"
                    trophyTitle={trophy.title}
                    kind="trophy"
                    photos={trophy.trophy_photos}
                    busy={busyKey === `${trophy.id}-trophy`}
                    onUpload={(files) => upload(trophy, "trophy", files)}
                    onRemove={(photo) => remove(trophy, photo, "trophy")}
                  />
                  <TrophyPhotoColumn
                    title="Winner photos"
                    trophyTitle={trophy.title}
                    kind="winner"
                    photos={trophy.winner_photos}
                    busy={busyKey === `${trophy.id}-winner`}
                    onUpload={(files) => upload(trophy, "winner", files)}
                    onRemove={(photo) => remove(trophy, photo, "winner")}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AdminEvents({ token, classes }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get("event");
  const [events, setEvents] = useState([]);
  const [entryTypes, setEntryTypes] = useState([]);
  const [eventClasses, setEventClasses] = useState([]);
  const [lunches, setLunches] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [ask, confirmDialog] = useConfirm();
  const [fileKey, setFileKey] = useState(0);
  const [form, setForm] = useState({
    title: "",
    date: "",
    description: "",
    entry_fee: "",
    spectator_fee: "",
    entry_types: [],
    image_main: null,
    image_secondary: null,
    image_mobile: null,
  });
  const [classForm, setClassForm] = useState(blankClassForm);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [formEvent, setFormEvent] = useState(null);
  const [classTarget, setClassTarget] = useState(null);
  const [showSaving, setShowSaving] = useState(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const selected = events.find((item) => String(item.id) === String(eventId));
  const view = eventId ? "edit" : "list";

  const fillEventForm = (item) => {
    setForm({
      title: item?.title || "",
      date: item?.date || "",
      description: item?.description || "",
      entry_fee: item?.entry_fee ?? "",
      spectator_fee: item?.spectator_fee ?? "",
      entry_types: (item?.entry_options || []).map((row) => String(row.id)),
      image_main: null,
      image_secondary: null,
      image_mobile: null,
    });
    setFileKey((current) => current + 1);
  };

  const load = async () => {
    const config = authConfig(token);
    const [eventData, classData, lunchData, typeData] = await Promise.all([
      axios.get("/api/admin/events/", config),
      axios.get("/api/admin/event-classes/", config),
      axios.get("/api/admin/vip-lunches/", config),
      axios.get("/api/admin/entry-types/", config),
    ]);
    setEvents(eventData.data);
    setEventClasses(classData.data);
    setLunches(lunchData.data);
    setEntryTypes(typeData.data);
  };

  useEffect(() => {
    const config = authConfig(token);
    Promise.all([
      axios.get("/api/admin/events/", config),
      axios.get("/api/admin/event-classes/", config),
      axios.get("/api/admin/vip-lunches/", config),
      axios.get("/api/admin/entry-types/", config),
    ])
      .then(([eventData, classData, lunchData, typeData]) => {
        setEvents(eventData.data);
        setEventClasses(classData.data);
        setLunches(lunchData.data);
        setEntryTypes(typeData.data);
      })
      .catch((err) => setError(errorText(err)));
  }, [token]);

  useEffect(() => {
    if (!selected) {
      return;
    }
    setForm({
      title: selected.title || "",
      date: selected.date || "",
      description: selected.description || "",
      entry_fee: selected.entry_fee ?? "",
      spectator_fee: selected.spectator_fee ?? "",
      entry_types: (selected.entry_options || []).map((row) => String(row.id)),
      image_main: null,
      image_secondary: null,
      image_mobile: null,
    });
  }, [selected]);

  const openList = () => {
    setNotice("");
    setError("");
    setSearchParams({ section: "events" });
  };

  const openAdd = () => {
    setNotice("");
    setError("");
    setFormEvent(null);
    fillEventForm(null);
    setEventFormOpen(true);
  };

  const openEditFields = (item) => {
    setNotice("");
    setError("");
    setFormEvent(item);
    fillEventForm(item);
    setEventFormOpen(true);
  };

  const closeEventForm = () => {
    setEventFormOpen(false);
    setFormEvent(null);
  };

  const openEvent = (id) => {
    setNotice("");
    setError("");
    setSearchParams({ section: "events", event: String(id) });
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleType = (id) => {
    const key = String(id);
    setForm((current) => ({
      ...current,
      entry_types: current.entry_types.includes(key)
        ? current.entry_types.filter((item) => item !== key)
        : [...current.entry_types, key],
    }));
  };

  const buildEventData = () => {
    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("date", form.date);
    formData.append("description", form.description);
    formData.append("entry_fee", form.entry_fee);
    formData.append("spectator_fee", form.spectator_fee);
    form.entry_types.forEach((id) => formData.append("entry_types", id));
    ["image_main", "image_secondary", "image_mobile"].forEach((name) => {
      if (form[name]) {
        formData.append(name, form[name]);
      }
    });
    return formData;
  };

  const saveNew = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const { data } = await axios.post("/api/admin/events/", buildEventData(), authConfig(token));
      await load();
      setNotice("Event saved.");
      closeEventForm();
      setSearchParams({ section: "events", event: String(data.id) });
    } catch (err) {
      setError(errorText(err));
    }
  };

  const saveExisting = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const target = formEvent || selected;
      await axios.put(`/api/admin/events/${target.id}/`, buildEventData(), authConfig(token));
      setFileKey((current) => current + 1);
      closeEventForm();
      setNotice("Event saved.");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeRow = async (url, label, name) => {
    if (!(await ask(removePrompt(name || `this ${label}`)))) {
      return;
    }
    setNotice("");
    setError("");
    try {
      await axios.delete(url, authConfig(token));
      if (label === "event") {
        setSearchParams({ section: "events" });
      }
      setNotice(`${label} removed.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const onChanged = async (message, id, label, name) => {
    if (id) {
      const urls = {
        "event class": `/api/admin/event-classes/${id}/`,
        "ticket type": `/api/admin/vip-ticket-types/${id}/`,
        luncheon: `/api/admin/vip-lunches/${id}/`,
      };
      await removeRow(urls[label], label, name);
      return;
    }
    setNotice(message);
    await load();
  };

  const saveClassEdit = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      await axios.put(
        `/api/admin/event-classes/${classTarget.id}/`,
        {
          car_class: classForm.car_class,
          section: classForm.section,
          number: classForm.number,
          parking_location: classForm.parking_location,
          trophies: (classTarget.trophies || []).map((trophy) => ({
            id: trophy.id,
            registration: classForm.trophies[trophy.id] || null,
          })),
        },
        authConfig(token, true)
      );
      setClassTarget(null);
      setNotice("Class saved.");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const toggleClassShow = async (item, name, show) => {
    setShowSaving(item.id);
    setNotice("");
    setError("");
    try {
      await axios.put(
        `/api/admin/event-classes/${item.id}/`,
        {
          car_class: item.car_class,
          section: item.section || "",
          number: item.number || "",
          parking_location: item.parking_location || "",
          show,
        },
        authConfig(token, true)
      );
      setNotice(
        show
          ? `${name} is on the registration form.`
          : `${name} is hidden from the registration form. Cars already entered stay on the event.`
      );
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setShowSaving(null);
    }
  };

  const applyTrophyPhotos = (trophyId, groups) => {
    const next = {
      trophy_photos: groups.trophy_photos || [],
      winner_photos: groups.winner_photos || [],
    };
    const patch = (item) => ({
      ...item,
      trophies: (item.trophies || []).map((trophy) => (trophy.id === trophyId ? { ...trophy, ...next } : trophy)),
    });
    setClassTarget((current) => (current && current !== "new" ? patch(current) : current));
    setEventClasses((rows) => rows.map((row) => (classTarget && row.id === classTarget.id ? patch(row) : row)));
  };

  const openNewClass = () => {
    setClassForm(blankClassForm());
    setClassTarget("new");
  };

  const openClassEdit = (item) => {
    const trophies = {};
    (item.trophies || []).forEach((trophy) => {
      trophies[trophy.id] = trophy.registration ? String(trophy.registration) : "";
    });
    setClassForm({
      car_class: String(item.car_class),
      section: item.section || "",
      number: item.number || "",
      parking_location: item.parking_location || "",
      trophies,
    });
    setClassTarget(item);
  };

  const addClass = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      await axios.post(
        "/api/admin/event-classes/",
        {
          car_class: classForm.car_class,
          section: classForm.section,
          number: classForm.number,
          parking_location: classForm.parking_location,
          event: selected.id,
        },
        authConfig(token, true)
      );
      setClassForm(blankClassForm());
      setClassTarget(null);
      setNotice("Class added to the event.");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const classesHere = eventClasses.filter((item) => String(item.event) === String(eventId));
  const attached = lunches.filter((lunch) => String(lunch.event) === String(eventId));

  const openVipAdd = () => {
    setSearchParams({ section: "vip", add: "1", event: String(eventId) });
  };

  const openVipEdit = (lunch) => {
    setSearchParams({ section: "vip", edit: String(lunch.id) });
  };

  return (
    <>
      {confirmDialog}
      <EventHeading view={view} onList={openList} />
      {notice && <Message variant="success">{notice}</Message>}
      {error && <Message variant="danger">{error}</Message>}
      <AdminFormModal
        show={eventFormOpen}
        size="lg"
        title={formEvent ? `Edit ${formEvent.title}` : "Add an event"}
        onHide={closeEventForm}
      >
        <Form onSubmit={formEvent ? saveExisting : saveNew}>
          <EventFields
            form={form}
            setField={setField}
            fileKey={fileKey}
            photos={formEvent}
            entryTypes={entryTypes}
            toggleType={toggleType}
          />
          <AdminFormActions saveLabel="Save event" onCancel={closeEventForm} />
        </Form>
      </AdminFormModal>

      {view === "list" && (
        <>
          <AdminAddButton onClick={openAdd}>Add an event</AdminAddButton>
          {events.length === 0 ? (
            <Message variant="info" className="mt-4">No events yet.</Message>
          ) : (
            <div className="event-cards">
              {events.map((item) => (
                <article key={item.id} className="event-card">
                  <button type="button" className="event-card-open" onClick={() => openEvent(item.id)}>
                    <img src={cardImage(item)} alt="" />
                    <span className="event-card-caption">
                      <strong>{item.title}</strong>
                      <span>{eventDateLabel(item.date)}</span>
                      {typeNames(item) ? <span>{typeNames(item)}</span> : null}
                    </span>
                  </button>
                  <span className="event-card-actions">
                    <RecordActions
                      name={item.title}
                      onEdit={() => openEvent(item.id)}
                      onRemove={() => removeRow(`/api/admin/events/${item.id}/`, "event", item.title)}
                    />
                  </span>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {view === "edit" && !selected && <Message variant="info">This event was not found.</Message>}

      {view === "edit" && selected && (
        <>
          <article className="admin-record">
            <div className="admin-record-copy">
              <strong>{selected.title}</strong>
              <span>
                {eventDateLabel(selected.date)}
                {typeNames(selected) ? ` · ${typeNames(selected)}` : ""}
              </span>
            </div>
            <RecordActions
              name={selected.title}
              onEdit={() => openEditFields(selected)}
              onRemove={() => removeRow(`/api/admin/events/${selected.id}/`, "event", selected.title)}
            />
          </article>

          <div className="event-split">
            <EventSection title="VIP luncheon" open={splitOpen} onToggle={() => setSplitOpen((value) => !value)}>
              <p className="vip-assign-note">
                A VIP luncheon is assigned to this event when the luncheon is created.{" "}
                <button type="button" className="vip-assign-link" onClick={openVipAdd}>
                  Add a luncheon here
                </button>
              </p>
              {attached.length === 0 ? (
                <p className="mb-0">No VIP luncheon on this event yet.</p>
              ) : (
                <ul className="vip-assigned-list">
                  {attached.map((lunch) => (
                    <li key={lunch.id} className="vip-assigned">
                      <span>
                        <strong>{lunch.title}</strong>
                        {lunch.hosted ? ` — ${lunch.hosted}` : ""}
                      </span>
                      <button
                        type="button"
                        className="vip-edit-jump"
                        aria-label={`Edit ${lunch.title}`}
                        title={`Edit ${lunch.title}`}
                        onClick={() => openVipEdit(lunch)}
                      >
                        Edit
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </EventSection>

            <EventSection
              title="Types on the registration form"
              open={splitOpen}
              onToggle={() => setSplitOpen((value) => !value)}
            >
            <p>
              Edit this event and check the types that should show. Add a new type under Types, then come back and check it.
            </p>
            {(selected.entry_options || []).length === 0 ? (
              <Message variant="info" className="mt-3">No types checked. The registration form has nothing to show until you check one.</Message>
            ) : (
              <p>{typeNames(selected)}</p>
            )}
            </EventSection>
          </div>

          <EventSchedule eventId={selected.id} token={token} />

          <EventRules eventId={selected.id} token={token} />

          <EventClubs eventId={selected.id} token={token} />

          <EventSection title="Classes">
            <p>
              Section, class number, and parking for each class at this event. A Concours class gets six trophies. Any other class gets a winner and a runner-up. Turn Show off to hide a class from new registrations. A class with a car entered, or a trophy assigned, stays on the event.
            </p>
            <div className="admin-sheet-wrap">
              <table className="admin-sheet">
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Number</th>
                    <th>Parking</th>
                    <th>Show</th>
                    <th>
                      <div className="admin-sheet-head">
                        <AdminAddButton onClick={openNewClass}>
                          <svg className="admin-sheet-add-icon" viewBox="0 0 16 16" aria-hidden="true">
                            <path fill="currentColor" d="M7.1 1.4h1.8v5h5v1.8h-5v5H7.1v-5h-5V6.4h5v-5z" />
                          </svg>
                          Add a class
                        </AdminAddButton>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {classesHere.length === 0 ? (
                    <tr>
                      <td colSpan={6}>No classes assigned yet.</td>
                    </tr>
                  ) : (
                    classesHere.map((item) => {
                      const name = classes.find((row) => String(row.id) === String(item.car_class))?.title || "this class";
                      const entered = item.entries || [];
                      const awarded = (item.trophies || []).some((trophy) => trophy.registration);
                      const hidden = item.show === false;
                      return (
                        <Fragment key={item.id}>
                        <tr>
                          <td className="admin-sheet-name">
                            {name}
                            {hidden ? (
                              <p className="class-hold-note">Hidden from the registration form.</p>
                            ) : null}
                            {hidden && entered.length > 0 ? (
                              <div className="class-hold-alert">
                                <strong>
                                  {entered.length === 1
                                    ? "Talk to a higher-up before checking this car in."
                                    : "Talk to a higher-up before checking these cars in."}
                                </strong>
                                <ul>
                                  {entered.map((entry) => (
                                    <li key={entry.id}>{entry.label}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </td>
                          <td>{item.section || ""}</td>
                          <td>{item.number || ""}</td>
                          <td>{item.parking_location || ""}</td>
                          <td>
                            <label className="class-show">
                              <input
                                type="checkbox"
                                checked={!hidden}
                                disabled={showSaving === item.id}
                                aria-label={`Show ${name} on the registration form`}
                                title={`Show ${name} on the registration form`}
                                onChange={(e) => toggleClassShow(item, name, e.target.checked)}
                              />
                              Show
                            </label>
                          </td>
                          <td>
                            <RecordActions
                              name={name}
                              onEdit={() => openClassEdit(item)}
                              onRemove={
                                awarded || entered.length > 0
                                  ? undefined
                                  : () => onChanged(null, item.id, "event class", name)
                              }
                            />
                          </td>
                        </tr>
                        {(() => {
                          const sheet = trophySheet(item.trophies);
                          const columns = `is-${sheet.places.length}`;
                          return (
                            <>
                              <tr className="class-trophy-head">
                                <td colSpan={6}>
                                  <div className={`class-trophy-grid ${columns}`}>
                                    <span>{sheet.heading}</span>
                                    {sheet.places.map((place) => (
                                      <span key={place}>{place}</span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                              {sheet.rows.map((row) => (
                                <tr key={`${item.id}-${row.key}`} className="class-trophy-row">
                                  <td colSpan={6}>
                                    <div className={`class-trophy-grid ${columns}`}>
                                      <strong>{row.label}</strong>
                                      {row.trophies.map((trophy) => (
                                        <span key={trophy.id}>{trophyHolder(trophy, entered)}</span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </>
                          );
                        })()}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <AdminFormModal
              show={Boolean(classTarget)}
              size="xl"
              title={classTarget === "new" ? "Add a class" : "Edit class"}
              onHide={() => setClassTarget(null)}
            >
              <Form onSubmit={classTarget === "new" ? addClass : saveClassEdit}>
                <Form.Group className="mb-3" controlId="assignClass">
                  <Form.Label>Car class</Form.Label>
                  <Form.Select
                    value={classForm.car_class}
                    onChange={(e) => setClassForm((current) => ({ ...current, car_class: e.target.value }))}
                    required
                  >
                    <option value="">Select a class</option>
                    {classes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.concours ? `${item.title} · Concours` : item.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="section">
                      <Form.Label>Section</Form.Label>
                      <Form.Control
                        value={classForm.section}
                        onChange={(e) => setClassForm((current) => ({ ...current, section: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="classNumber">
                      <Form.Label>Number</Form.Label>
                      <Form.Control
                        value={classForm.number}
                        onChange={(e) => setClassForm((current) => ({ ...current, number: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="parking">
                      <Form.Label>Parking</Form.Label>
                      <Form.Control
                        value={classForm.parking_location}
                        onChange={(e) =>
                          setClassForm((current) => ({ ...current, parking_location: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {classTarget && classTarget !== "new" && classTarget.show === false && (classTarget.entries || []).length > 0 ? (
                  <div className="class-hold-alert mb-3">
                    <strong>
                      {(classTarget.entries || []).length === 1
                        ? "Talk to a higher-up before checking this car in."
                        : "Talk to a higher-up before checking these cars in."}
                    </strong>
                    <ul>
                      {classTarget.entries.map((entry) => (
                        <li key={entry.id}>{entry.label}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {classTarget === "new" && classForm.car_class ? (
                  <p className="field-note">
                    {classes.find((item) => String(item.id) === String(classForm.car_class))?.concours
                      ? "This Concours class gets Street and Concours trophies, first through third."
                      : "This class gets a winner and a runner-up."}
                  </p>
                ) : null}
                {classTarget && classTarget !== "new" ? (
                  <p className="field-note">
                    {(classTarget.trophies || []).some((trophy) => trophy.division)
                      ? "Street trophies are for Street/Race entries. Concours trophies are for Concours entries. A display entry cannot take a trophy, and a car can take only one."
                      : "A display entry cannot take a trophy, and a car can take only one."}
                  </p>
                ) : null}
                {classTarget && classTarget !== "new" ? (
                  <TrophySlots
                    key={classTarget.id}
                    trophies={classTarget.trophies}
                    entries={classTarget.entries}
                    selected={classForm.trophies}
                    token={token}
                    onSelect={(trophyId, value) =>
                      setClassForm((form) => ({
                        ...form,
                        trophies: { ...form.trophies, [trophyId]: value },
                      }))
                    }
                    onPhotos={applyTrophyPhotos}
                  />
                ) : null}
                <AdminFormActions saveLabel={classTarget === "new" ? "Add class" : "Save class"} onCancel={() => setClassTarget(null)} />
              </Form>
            </AdminFormModal>
          </EventSection>

          {selected && <EventGallery eventId={selected.id} token={token} />}
        </>
      )}
    </>
  );
}

export default AdminEvents;
